import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { companySubscriptionSeatId } from '../AppShell/companyAdminAccess';
import { CompanyAdminGate } from './CompanyAdminGate';
import {
  assignCompanySeat,
  companyAdminError,
  loadCompanyPlans,
  loadCompanyRoster,
  type CompanyPlanRow,
  type CompanyRosterRow,
} from './companyAdminApi';

const { Title, Text } = Typography;

const SubscriptionsBody: React.FC<{ companyId: string; companyName: string }> = ({
  companyId,
  companyName,
}) => {
  const [plans, setPlans] = useState<CompanyPlanRow[]>([]);
  const [roster, setRoster] = useState<CompanyRosterRow[]>([]);
  const [planId, setPlanId] = useState<string | undefined>();
  const [userId, setUserId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [planRows, people] = await Promise.all([
        loadCompanyPlans(companyId),
        loadCompanyRoster(companyId),
      ]);
      setPlans(planRows);
      setRoster(people);
    } catch (err) {
      setPlans([]);
      setRoster([]);
      setError(companyAdminError(err, 'Could not load company subscriptions.'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const assign = async () => {
    setError(null);
    const plan = plans.find((row) => row.id === planId);
    if (!plan || !userId) {
      setError('Choose a company plan and a member of this company.');
      return;
    }
    let seatId = '';
    try {
      seatId = companySubscriptionSeatId(plan);
    } catch (err) {
      setError(companyAdminError(err, 'This plan cannot be used for seat assignment.'));
      return;
    }
    setAssigning(true);
    try {
      await assignCompanySeat(seatId, userId);
      setUserId(undefined);
    } catch (err) {
      setError(
        companyAdminError(
          err,
          `Seat assignment failed for company_subscriptions.id ${seatId}.`,
        ),
      );
    } finally {
      setAssigning(false);
    }
  };

  const columns: ColumnsType<CompanyPlanRow> = [
    {
      title: 'Plan',
      key: 'name',
      render: (_, row) =>
        row.subscription_type?.subscription_name ||
        row.subscription_type?.subscription_code ||
        row.id,
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (value) => (value === false ? 'No' : 'Yes'),
    },
    { title: 'Company subscription id', dataIndex: 'id', key: 'id' },
  ];

  return (
    <Card>
      <Title level={3}>Subscriptions</Title>
      <Text type="secondary">
        Plans for {companyName}. Seat assignment uses company_subscriptions.id. This page does not create a plan and does not create a user-owned subscription. New company plans are created on Platform administration → Subscriptions.
      </Text>
      <Space direction="vertical" style={{ width: '100%', marginTop: 16 }} size="middle">
        {error ? <Alert type="error" showIcon message={error} /> : null}
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={plans}
          pagination={false}
          locale={{ emptyText: loading ? 'Loading' : 'No subscriptions for this company.' }}
        />
        <Space wrap>
          <Select
            placeholder="Company plan"
            style={{ minWidth: 280 }}
            value={planId}
            onChange={setPlanId}
            options={plans.map((row) => ({
              value: row.id,
              label:
                row.subscription_type?.subscription_name ||
                row.subscription_type?.subscription_code ||
                row.id,
            }))}
          />
          <Select
            placeholder="Member"
            style={{ minWidth: 280 }}
            value={userId}
            onChange={setUserId}
            options={roster.map((row) => ({
              value: row.user_id,
              label: row.email || row.full_name || row.username || row.user_id,
            }))}
          />
          <Button type="primary" loading={assigning} onClick={() => void assign()}>
            Assign seat
          </Button>
        </Space>
      </Space>
    </Card>
  );
};

export const CompanyAdminSubscriptionsPage: React.FC = () => (
  <CompanyAdminGate>
    {(target) => <SubscriptionsBody companyId={target.companyId} companyName={target.companyName} />}
  </CompanyAdminGate>
);

export default CompanyAdminSubscriptionsPage;
